import { FC, useRef, useState } from "react"
import { Info } from "lucide-react"
import {
  ConnectorFormValues,
  ConnectorDirection
} from "../../../../../../../types"
import { FormikErrors } from "formik"
import ConnectorFormInput from "../../components/ConnectorFormInput"
import LeadScoreSchemaPreview from "../../lead-score-schema-preview"
import PendingSubscriptionSchemaPreview from "../../pending-subscription-schema-preview"
import TemplateEditor from "../../template-editor"

const LEAD_SCORE_CATEGORIES = [
  {
    value: "hot_lead",
    label: "Hot Lead",
    description:
      "High intent, ready to act now. Strong buying signals like recent rate checks, payment calculations, or repeated quote views. Contact immediately — these convert if reached within minutes, not hours."
  },
  {
    value: "needs_contact",
    label: "Needs Contact",
    description:
      "Qualified and interested but hasn't been reached yet. Has engaged meaningfully (filled out info, viewed multiple rates) but no LO outreach has happened. Top priority for the next call queue."
  },
  {
    value: "engaged",
    label: "Engaged",
    description:
      "Actively researching and returning to the site, but not yet showing urgency signals. Good candidates for nurture content, rate alerts, and check-in calls. Keep warm — they're shopping."
  },
  {
    value: "dormant",
    label: "Dormant",
    description:
      "Previously engaged but has gone quiet. No recent activity or opens. Worth a re-engagement touch (new rate, market update) to see if their situation has changed, but don't prioritize over active leads."
  },
  {
    value: "at_risk",
    label: "At Risk",
    description:
      "Showing signs of dropping off or going to a competitor — e.g., declining engagement, unsubscribed from emails, or stalled mid-process. Needs a save attempt: personal outreach, better offer, or address whatever objection caused the cooldown."
  }
] as const

interface ConfigurationSectionProps {
  values: ConnectorFormValues
  setFormField: (name: string, val: any) => void
  isCreate: boolean
  errors: FormikErrors<ConnectorFormValues>
  onContentTypeChange: (contentType: string) => void
  onDataTypeChange: (dataType: string) => void
  availableTemplates: Array<{ name: string; field_mapping: any; type: string }>
  onTemplateLoad: (templateName: string) => void
}

const ConfigurationSection: FC<ConfigurationSectionProps> = ({
  values,
  setFormField,
  isCreate,
  errors,
  onContentTypeChange,
  onDataTypeChange,
  availableTemplates,
  onTemplateLoad
}) => {
  const previousDirectionRef = useRef<ConnectorDirection>(values.direction)
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null)
  const activeCategory =
    LEAD_SCORE_CATEGORIES.find(c => c.value === hoveredCategory) ??
    LEAD_SCORE_CATEGORIES[0]

  const handleDirectionChange = (newDirection: ConnectorDirection) => {
    const hasFieldMapping =
      values.field_mapping && values.field_mapping.length > 0

    if (hasFieldMapping && previousDirectionRef.current !== newDirection) {
      const confirmed = window.confirm(
        "Changing the direction will clear all field mapping settings. Are you sure you want to continue?"
      )

      if (confirmed) {
        setFormField("direction", newDirection)
        setFormField("field_mapping", [])
        previousDirectionRef.current = newDirection
      }
    } else {
      setFormField("direction", newDirection)
      previousDirectionRef.current = newDirection
    }
  }

  return (
    <div className="border-b pb-6">
      <h3 className="text-lg font-medium mb-4">Configuration</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Direction *
          </label>
          {isCreate ? (
            <>
              <select
                value={values.direction}
                onChange={e =>
                  handleDirectionChange(e.target.value as ConnectorDirection)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="outgoing">→ Outgoing (BB → CRM)</option>
                <option value="incoming">← Incoming (CRM → BB)</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                {values.direction === "outgoing" &&
                  "Sends data to your CRM when records are created in Banking Bridge"}
                {values.direction === "incoming" &&
                  "Receives data from your CRM to create records in Banking Bridge"}
              </p>
            </>
          ) : (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
              {values.direction === "outgoing" && "→ Outgoing (BB → CRM)"}
              {values.direction === "incoming" && "← Incoming (CRM → BB)"}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data Type *
          </label>
          {isCreate ? (
            <select
              value={values.data_type}
              onChange={e => onDataTypeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="lead">Lead</option>
              <option value="subscription">Subscription</option>
              <option value="pending_subscription">Pending Subscription</option>
              {values.direction === "outgoing" && (
                <option value="lead_score">Lead Score</option>
              )}
            </select>
          ) : (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
              {values.data_type === "lead" && "Lead"}
              {values.data_type === "subscription" && "Subscription"}
              {values.data_type === "pending_subscription" &&
                "Pending Subscription"}
              {values.data_type === "lead_score" && "Lead Score"}
            </div>
          )}
        </div>

        {values.data_type === "pending_subscription" &&
          values.direction === "outgoing" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                pending_subscription.converted
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Fires when a pending rate subscription is converted to a real
                rate subscription
              </p>
            </div>
          )}

        {values.data_type === "lead_score" && (
          <div className="space-y-4 bg-gray-50 rounded-md p-4">
            <h4 className="text-sm font-medium text-gray-700">
              Lead Score Filters
            </h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Score (0-100)
              </label>
              <input
                type="number"
                min={0}
                max={100}
                value={values.config?.min_score ?? ""}
                onChange={e => {
                  const val = e.target.value
                  setFormField("config", {
                    ...values.config,
                    min_score: val === "" ? undefined : Number(val)
                  })
                }}
                placeholder="e.g. 70"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <p className="text-sm text-gray-500 mt-1">
                Only fire this connector when the intent score is at or above
                this value
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {LEAD_SCORE_CATEGORIES.map(cat => {
                  const selected =
                    values.config?.categories?.includes(cat.value) ?? false
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onMouseEnter={() => setHoveredCategory(cat.value)}
                      onMouseLeave={() => setHoveredCategory(null)}
                      onFocus={() => setHoveredCategory(cat.value)}
                      onBlur={() => setHoveredCategory(null)}
                      onClick={() => {
                        const current = values.config?.categories ?? []
                        const next = selected
                          ? current.filter(c => c !== cat.value)
                          : [...current, cat.value]
                        setFormField("config", {
                          ...values.config,
                          categories: next.length > 0 ? next : undefined
                        })
                      }}
                      className={`px-3 py-1.5 border rounded-full text-sm font-medium transition-colors ${
                        selected
                          ? "bg-primary-50 border-primary-400 text-primary-700"
                          : "border-gray-300 text-gray-500 hover:border-gray-400"
                      }`}
                    >
                      {cat.label}
                    </button>
                  )
                })}
              </div>

              {/* Hover/focus hint panel — defaults to first category when nothing is hovered */}
              <div className="mt-3 rounded-md border border-gray-200 bg-white px-3.5 py-3">
                <div className="flex items-start gap-2.5">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-400" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                      {activeCategory.label}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      {activeCategory.description}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-2">
                Only fire for these categories. Leave empty to fire for all
                categories.
              </p>
            </div>
          </div>
        )}

        {values.direction === "outgoing" && (
          <>
            <ConnectorFormInput
              label="CRM Webhook URL"
              value={values.webhook_url || ""}
              onChange={value => setFormField("webhook_url", value)}
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              error={errors.webhook_url}
              helpText="The URL where Banking Bridge will send data"
              type="url"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content Type *
              </label>
              <select
                value={
                  values.data_type === "lead_score" ||
                  values.data_type === "pending_subscription"
                    ? "application/json"
                    : values.config?.content_type || "application/json"
                }
                onChange={e => onContentTypeChange(e.target.value)}
                disabled={
                  values.data_type === "lead_score" ||
                  values.data_type === "pending_subscription"
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                <option value="application/json">
                  JSON (application/json)
                </option>
                <option value="application/x-www-form-urlencoded">
                  URL-Encoded (application/x-www-form-urlencoded)
                </option>
                <option value="multipart/form-data">
                  Form Data (multipart/form-data)
                </option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                {values.data_type === "lead_score" ||
                values.data_type === "pending_subscription"
                  ? "This connector always sends JSON — content type is fixed."
                  : "How data will be formatted when sent to your CRM (automatically sets Content-Type header)"}
              </p>
            </div>

            {values.data_type === "lead_score" ? (
              <LeadScoreSchemaPreview />
            ) : values.data_type === "pending_subscription" ? (
              <PendingSubscriptionSchemaPreview />
            ) : (
              <div className="border-t pt-4">
                <TemplateEditor
                  template={values.template || ""}
                  onChange={value => setFormField("template", value)}
                  dataType={values.data_type}
                  contentType={values.config?.content_type}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ConfigurationSection
